import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import type { AxiosError } from "axios";
import {
  getAllCoursesService,
  getCourseService,
  type CsViewType,
  getUnitScormFileService,
  updateScormDataService,
  updateScormStatusService,
} from "@/service/course.service";
import {
  CourseModel,
  CourseModelList,
  CourseQuery,
  UnitScormFile,
  UpdateScormData,
  UpdateScormStatus,
} from "@/types/course.model";
import { markNotificationAsRead } from "@/service/notification.service";
import { getScormRefetchParams } from "@/utils/scormWebhookPayload";
import { refetchCourseCount } from "./authSlice";

// Re-export types for components to use
export type {
  CourseModel,
  CourseModelList,
  CourseQuery,
  UnitScormFile,
  UpdateScormData,
} from "@/types/course.model";

// State interface
interface CourseState {
  courses: CourseModelList[];
  currentCourse: CourseModel | null;
  currentCourseCount: CourseModel | null;
  currentScormFile: UnitScormFile | null | undefined;
  loading: boolean;
  scormLoading: boolean; // Separate loading state for SCORM files
  error: string | null;
  navigationSource: string; // Track where the navigation came from
  noCourses: boolean;
  noCourseData: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

// Initial state
const initialState: CourseState = {
  courses: [],
  currentCourse: null,
  currentCourseCount: null,
  currentScormFile: null,
  loading: true,
  scormLoading: false, // Initialize scormLoading state
  error: null,
  navigationSource: "", // Empty string means no specific source
  noCourses: true,
  noCourseData: false, // No course data available
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// No mock data or service functions here - they've been moved to the service files

// Async thunks
export const fetchCourses = createAsyncThunk(
  "course/fetchAll",
  async (query: CourseQuery, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const state = getState() as RootState;

      const { school_id, student_id, teacher_id } = state.auth.user || {};

      if (school_id) {
        query.schoolId = school_id;
      }

      if (student_id) {
        query.studentId = student_id;
      }

      if (teacher_id) {
        query.teacherId = teacher_id;
      }

      const {
        data: { data, pageInfo },
      } = await getAllCoursesService(query);
      dispatch(setLoading(false));

      dispatch(setNoCourses(false));

      return {
        courses: data,
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoCourses(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch courses";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const fetchCourseById = createAsyncThunk(
  "course/fetchById",
  async (
    {
      gradeId,
      studentId,
      viewType = "course",
    }: {
      gradeId: string;
      studentId: string | null;
      viewType?: CsViewType;
    },
    { dispatch }
  ) => {
    try {
      dispatch(setLoading(true));
      const response = await getCourseService(gradeId, studentId, viewType);
      dispatch(setNoCourseData(false));
      dispatch(setLoading(false));
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoCourses(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message ||
        `Failed to fetch course with ID ${gradeId}`;
      dispatch(setError(message));
      throw error;
    }
  }
);

export const fetchUnitScormFile = createAsyncThunk(
  "course/fetchScormFile",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setScormLoading(true)); // Use scormLoading instead of general loading
      const { data } = await getUnitScormFileService(id);

      dispatch(setScormLoading(false));
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message ||
        `Failed to fetch scorm file for unit ID ${id}`;
      dispatch(setError(message));
      dispatch(setScormLoading(false)); // Make sure to set loading to false on error
      throw error;
    }
  }
);

export const updateScormData = createAsyncThunk(
  "course/updateScormData",
  async (
    { id, payload }: { id: string; payload: Omit<UpdateScormData, "id"> },
    { dispatch }
  ) => {
    try {
      dispatch(setLoading(true));
      const response = await updateScormDataService(id, payload);
      dispatch(setLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message ||
        `Failed to update scorm data for ID ${id}`;
      dispatch(setError(message));
      throw error;
    }
  }
);

export const updateScormStatusAction = createAsyncThunk(
  "course/updateScormStatus",
  async (payload: UpdateScormStatus, { dispatch }) => {
    try {
      // dispatch(s(true));
      const response = await updateScormStatusService(payload);
      const { gradeId, viewType } = getScormRefetchParams(payload);
      setTimeout(() => {
        dispatch(
          refetchLessonUnitCount({
            gradeId,
            studentId: null,
            viewType,
          })
        );
      }, 2000);
      // dispatch(setLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || `Failed to update scorm status}`;
      dispatch(setError(message));
      throw error;
    }
  }
);

export const refetchLessonUnitCount = createAsyncThunk(
  "course/refetchLessonUnitCount",
  async (
    payload: {
      gradeId: string;
      studentId: string | null;
      viewType?: CsViewType;
    },
    { dispatch }
  ) => {
    try {
      const response = await getCourseService(
        payload.gradeId,
        payload.studentId,
        payload.viewType ?? "course"
      );

      dispatch(setNotificationCounts(response.data.data));

      return null;
    } catch (error) {
      console.log("Error fetching user data:", error);

      throw error;
    }
  }
);

export const markNotificationAsReadAction = createAsyncThunk(
  "course/markNotificationAsRead",
  async (
    payload: Partial<{
      assignment_id?: string;
      student_id?: string;
      query_id?: string;
      gradeId?: string;
      viewType?: CsViewType;
    }>,
    { dispatch }
  ) => {
    try {
      const {
        data: { data },
      } = await markNotificationAsRead({
        assignment_id: payload.assignment_id,
        student_id: payload.student_id,
        query_id: payload.query_id,
      });

      dispatch(refetchCourseCount());
      if (payload.gradeId) {
        dispatch(
          refetchLessonUnitCount({
            gradeId: payload.gradeId,
            studentId: null,
            viewType: payload.viewType ?? "course",
          })
        );
      }

      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message ||
        "Failed to mark notification as read";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Course slice
const courseSlice = createSlice({
  name: "course",
  initialState,
  reducers: {
    setNoCourseData: (state, action: PayloadAction<boolean>) => {
      state.noCourseData = action.payload;
    },
    setNoCourses: (state, action: PayloadAction<boolean>) => {
      state.noCourses = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setScormLoading: (state, action: PayloadAction<boolean>) => {
      state.scormLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setNotificationCounts: (state, action: PayloadAction<CourseModel>) => {
      state.currentCourseCount = action.payload;
    },
    resetCurrentCourse: (state) => {
      state.currentCourse = null;
    },
    resetCurrentScormFile: (state) => {
      state.currentScormFile = null;
    },
    resetCourseDetail: (state) => {
      state.currentCourse = null;
      state.currentCourseCount = null;
      state.currentScormFile = null;
      // state.navigationSource = "";
      state.noCourseData = false;
      state.loading = true;
    },
    resetAllState: (): CourseState => initialState,
    setNavigationSource: (state, action: PayloadAction<string>) => {
      state.navigationSource = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch courses cases
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.courses = action.payload.courses;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch courses";
      })
      // Fetch course by ID cases
      .addCase(fetchCourseById.fulfilled, (state, action) => {
        state.currentCourse = action.payload;
        state.currentCourseCount = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCourseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch course";
      })
      // Fetch scorm file cases
      .addCase(fetchUnitScormFile.fulfilled, (state, action) => {
        state.currentScormFile = action.payload;
        state.scormLoading = false;
        state.error = null;
      })
      .addCase(fetchUnitScormFile.rejected, (state, action) => {
        state.scormLoading = false;
        state.error = action.error.message || "Failed to fetch scorm file";
      })
      // Update scorm data cases
      .addCase(updateScormData.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateScormData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update scorm data";
      })
      .addCase(markNotificationAsReadAction.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(markNotificationAsReadAction.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to mark notification as read";
      })
      .addCase(updateScormStatusAction.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(updateScormStatusAction.rejected, (state, action) => {
        state.error = action.error.message || "Failed to update scorm status";
      });
  },
});

// Export actions
export const {
  setNoCourses,
  setNoCourseData,
  setLoading,
  setScormLoading,
  setError,
  clearError,
  resetCurrentCourse,
  resetCurrentScormFile,
  resetAllState,
  setNavigationSource,
  setNotificationCounts,
  resetCourseDetail,
} = courseSlice.actions;

// Selectors
export const selectCourses = (state: RootState) => state.course.courses;
export const selectCurrentCourse = (state: RootState) =>
  state.course.currentCourse;
export const selectCurrentScormFile = (state: RootState) =>
  state.course.currentScormFile;
export const selectCourseLoading = (state: RootState) => state.course.loading;
export const selectScormLoading = (state: RootState) =>
  state.course.scormLoading;
export const selectCourseError = (state: RootState) => state.course.error;
export const selectTotalItems = (state: RootState) =>
  state.course.pagination.totalItems;
export const selectPagination = (state: RootState) => state.course.pagination;
export const selectNavigationSource = (state: RootState) =>
  state.course.navigationSource;
export const selectNoCourses = (state: RootState) => state.course.noCourses;
export const selectCurrentCourseCount = (state: RootState) =>
  state.course.currentCourseCount;
export const selectCourseTabCount = (state: RootState) =>
  state.course.currentCourseCount?.notify;
export const selectNoCourseData = (state: RootState) =>
  state.course.noCourseData;

export default courseSlice.reducer;
